import _, { isPlainObject } from 'lodash';
import ReactGA from 'react-ga';
import httpClient, { getRequestSignature } from '../clientSideServices/http';
import { getUserAssociatedStoreId } from '../clientSideServices/users';
import { isProd, isStaging } from '../config';
import { i18n } from '../i18n';
import {
    AnalyticEvent,
    EyeballLoggerPayload,
    EyeballTimeView,
    IStore,
    Source
} from '../interfaces';
import { sessionKey } from '../utils/analyticsSession';
import { getNow } from '../utils/clock';
import {
    DID_START_SESSION,
    DID_VIEW_PAGE,
    EYEBALL_TIME,
    GA_INITIALIZED
} from '../utils/constants';
import { getIdentityId } from '../utils/identity';
import { generateV4UUID } from '../utils/identityGenerator';
import { PageView } from '../utils/pageView';
import {
    getUrlWithParentWindowQueryParams,
    tryGetParentWindowField
} from '../utils/window';
import {
    CUSTOM_ANALYTICS_EVENT,
    DID_CLICK_BUTTON,
    DID_KEY_PRESS,
    DID_VIEW_CONTENT,
    JITSI_MEET_EVENT
} from './../utils/constants';
import { isOnClientSide } from './../utils/window';

let events: AnalyticEvent[] = [];
const eventDeduplicationMap = new Map<string, number>(); // Track recent events to prevent duplicates
const DEDUPLICATION_WINDOW = 1000; // 1 second window for deduplication

export const getAELeft = (): AnalyticEvent[] => {
  let eventsLeft;
  try {
    eventsLeft = JSON.parse(localStorage.getItem(CUSTOM_ANALYTICS_EVENT));
  } catch (e) {
    return [];
  }
  return eventsLeft;
};

const noDelayEvents = [EYEBALL_TIME, DID_VIEW_PAGE, DID_VIEW_CONTENT];

const emptyAELeft = (): void => {
  try {
    localStorage.removeItem(CUSTOM_ANALYTICS_EVENT);
  } catch (e) {
    console.error(e);
  }
};

export const logCustomAnalytics = (events: AnalyticEvent[]) => {
  if (!events || events.length === 0) return;
  
  //create diffrent timestamps for each event
  const _events = events.map((ev, index) => ({
    ...ev,
    event_timestamp: ev.event_timestamp + index
  }));
  
  // Add retry logic for failed requests
  const sendAnalytics = async (retryCount = 0) => {
    try {
      // Use requestIdleCallback for non-critical analytics to avoid blocking UI
      const sendRequest = () => httpClient.post('/api/analytics', _events);
      
      if ('requestIdleCallback' in window && retryCount === 0) {
        // Only use requestIdleCallback for initial requests, not retries
        return new Promise((resolve, reject) => {
          requestIdleCallback(() => {
            sendRequest().then(resolve).catch(reject);
          }, { timeout: 2000 });
        });
      } else {
        return await sendRequest();
      }
    } catch (e) {
      console.warn(`Analytics request failed (attempt ${retryCount + 1}):`, e);
      
      // Retry up to 2 times with exponential backoff
      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s
        setTimeout(() => sendAnalytics(retryCount + 1), delay);
      } else {
        // Store failed events in localStorage for recovery
        try {
          const failedEvents = JSON.parse(localStorage.getItem('failed_analytics_events') || '[]');
          failedEvents.push(..._events);
          localStorage.setItem('failed_analytics_events', JSON.stringify(failedEvents));
        } catch (storageError) {
          console.error('Failed to store analytics events:', storageError);
        }
        
        logException(
          `logCustomAnalytics failed after retries: ${JSON.stringify(e)}`
        );
      }
    }
  };
  
  sendAnalytics();
};

export const flush = () => {
  if (events.length) {
    logCustomAnalytics([...events]);
  }
  events = [];
};

export const addEventToQueue = (event: AnalyticEvent, flushSoon?: boolean) => {
  // Deduplicate events to prevent spam
  const eventKey = `${event.type}-${event.payload?.action || ''}-${event.payload?.id || ''}`;
  const now = Date.now();
  
  // Clean up old entries from deduplication map
  eventDeduplicationMap.forEach((timestamp, key) => {
    if (now - timestamp > DEDUPLICATION_WINDOW) {
      eventDeduplicationMap.delete(key);
    }
  });
  
  // Check if this event was recently sent
  const lastSent = eventDeduplicationMap.get(eventKey);
  if (lastSent && (now - lastSent) < DEDUPLICATION_WINDOW) {
    // Skip duplicate event
    return;
  }
  
  // Mark this event as sent
  eventDeduplicationMap.set(eventKey, now);
  
  events = [...events, event];
  // Reduce threshold to 3 events for more frequent flushing
  if (events.length >= 3 || flushSoon) {
    flush();
  }
};

if (isOnClientSide()) {
  let isFlushing = false;
  let flushTimeout: NodeJS.Timeout | null = null;
  let lastFlushTime = 0;
  const MIN_FLUSH_INTERVAL = 200; // Minimum 200ms between flushes

  const safeFlush = () => {
    const now = Date.now();
    if (isFlushing || events.length === 0 || (now - lastFlushTime) < MIN_FLUSH_INTERVAL) {
      return;
    }
    
    isFlushing = true;
    lastFlushTime = now;
    
    // Use requestIdleCallback for non-critical flushes to avoid blocking UI
    const flushWithIdleCallback = () => {
      try {
        flush();
      } catch (error) {
        console.warn('Analytics flush failed:', error);
      } finally {
        isFlushing = false;
      }
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(flushWithIdleCallback, { timeout: 1000 });
    } else {
      // Fallback to setTimeout for browsers without requestIdleCallback
      setTimeout(flushWithIdleCallback, 0);
    }
  };

  // Debounced flush to prevent excessive API calls
  const debouncedFlush = () => {
    if (flushTimeout) {
      clearTimeout(flushTimeout);
    }
    flushTimeout = setTimeout(safeFlush, 150); // Increased debounce time
  };

  // Throttled flush for rapid events
  let throttleTimeout: NodeJS.Timeout | null = null;
  const throttledFlush = () => {
    if (throttleTimeout) return;
    throttleTimeout = setTimeout(() => {
      safeFlush();
      throttleTimeout = null;
    }, 100);
  };

  // Existing beforeunload listener with immediate flush
  window.addEventListener('beforeunload', function () {
    localStorage.setItem(CUSTOM_ANALYTICS_EVENT, JSON.stringify(events));
    // Force immediate flush on page unload (critical)
    if (events.length > 0) {
      logCustomAnalytics([...events]);
    }
  });

  // Add visibility change handler with throttling
  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'hidden') {
      throttledFlush(); // Use throttled instead of debounced for better performance
    }
  });

  // Add blur handler for tab switching
  window.addEventListener('blur', function() {
    throttledFlush();
  });

  // Add pagehide event for better unload handling
  window.addEventListener('pagehide', function() {
    if (events.length > 0) {
      localStorage.setItem(CUSTOM_ANALYTICS_EVENT, JSON.stringify(events));
      // Use sendBeacon for more reliable delivery on page unload
      try {
        const data = JSON.stringify(events);
        navigator.sendBeacon('/api/analytics', data);
      } catch (e) {
        console.warn('sendBeacon failed, falling back to localStorage');
      }
    }
  });

  // Existing load listener
  window.addEventListener('load', function () {
    const eventsLeft = getAELeft();
    if (eventsLeft?.length) {
      logCustomAnalytics(eventsLeft);
    }
    emptyAELeft();

    // Recover failed analytics events
    try {
      const failedEvents = JSON.parse(localStorage.getItem('failed_analytics_events') || '[]');
      if (failedEvents.length > 0) {
        logCustomAnalytics(failedEvents);
        localStorage.removeItem('failed_analytics_events');
      }
    } catch (e) {
      console.warn('Failed to recover analytics events:', e);
    }

    // More frequent flushing interval with requestIdleCallback
    setInterval(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(safeFlush, { timeout: 2000 });
      } else {
        safeFlush();
      }
    }, 5000); // Reduced from 10s to 5s
  });
}

const inspifyGa = 'UA-162755127-1';

export const initGA = (ga = inspifyGa) => {
  if (isProd && !window[GA_INITIALIZED]) {
    ReactGA.initialize(ga);
    window[GA_INITIALIZED] = true;
  }
};

const logOnlyGAInitialized = (f: () => void) => {
  if (window[GA_INITIALIZED]) {
    f();
  }
};

const logPageViewGA = (pathname: string) => {
  logOnlyGAInitialized(() => {
    ReactGA.set({ page: pathname });
    ReactGA.pageview(pathname);
  });
};

const logEventGA = (category: string, action: string) => {
  logOnlyGAInitialized(() => {
    ReactGA.event({ category, action });
  });
};

const logExceptionGA = (description: string, fatal: boolean) => {
  logOnlyGAInitialized(() => {
    ReactGA.exception({ description, fatal });
  });
};

export const logPageView = (
  storeId: string | undefined = undefined,
  pageView: PageView
) => {
  
  logPageViewGA(window.location.pathname);
  // Force immediate flush for critical page view events
  logCustomEvent(
    DID_VIEW_PAGE,
    { url: window.location.href, pageType: pageView },
    storeId,
    true // noDelay = true for immediate flush
  );
};

export const logSessionStart = (sessionId: string, storeId?: string) => {
  //console.log(`session start ${sessionId}`);
  logCustomEvent(DID_START_SESSION, { sessionId }, storeId);
};

export const getStoreIdFromUrl = () => {
  const regex = /store=([a-zA-Z0-9-]+)/;
  const match = window.location.search.match(regex);
  if (match && match.length === 2) {
    return match[1];
  }
  return null;
};

export const isHubPage = (pathname: string, search: string) => {
  const path = pathname?.replace('#', '').split('/');
  return (
    path?.[1] === 'hub' ||
    path?.[1] === 'nudge' ||
    search.indexOf(`source=${Source.Nudge}`) > -1 ||
    search.indexOf(`source=${Source.AdvisorHub}`) > -1
  );
};

const getEnv = () => {
  if (isProd) return 'prod';
  if (isStaging) return 'staging';
  return 'dev';
};

// Cache for expensive operations (only storeId, geolocation is already cached)
let cachedStoreId: string | undefined = undefined;
let cachedReduxStoreId: string | undefined = undefined;
let lastCacheTime = 0;
const CACHE_DURATION = 5000; // 5 seconds

const getCachedStoreId = (storeId?: string): string | undefined => {
  const now = Date.now();
  if (now - lastCacheTime > CACHE_DURATION) {
    // Clear cache
    cachedStoreId = undefined;
    cachedReduxStoreId = undefined;
    lastCacheTime = now;
  }

  if (storeId) return storeId;
  if (cachedStoreId) return cachedStoreId;

  // Cache expensive operations
  const urlStoreId = getStoreIdFromUrl();
  const windowStoreId = window['storeId'];
  const store = window['store'] as IStore | undefined;
  const storeIdFromStore = store?.id;

  // Only access Redux store if not cached
  if (!cachedReduxStoreId) {
    try {
      if (typeof window !== 'undefined' && window['__NEXT_REDUX_WRAPPER_STORE__']) {
        const reduxState = window['__NEXT_REDUX_WRAPPER_STORE__'].getState();
        cachedReduxStoreId = reduxState?.clientState?.store?.id || 
                            reduxState?.clientState?.vb?.store?.id ||
                            reduxState?.clientState?.teleport?.teleportTo?.store?.id;
      }
    } catch (e) {
      // Silently fail if Redux store is not available
    }
  }

  cachedStoreId = urlStoreId || storeIdFromStore || windowStoreId || cachedReduxStoreId;
  return cachedStoreId;
};

export const getAnalyticEvent = (
  category: string,
  payload: any,
  storeId: string | undefined = undefined
) => {
  const startTime = performance.now();
  
  // Use cached values for better performance
  const _storeId = getCachedStoreId(storeId);
  
  // Get geolocation data from sessionStorage (already cached by geolocation service)
  let geolocationData: { 
    country?: string; 
    city?: string; 
    country_code?: string; 
    country_name?: string;
    continent_code?: string;
    continent_name?: string;
    region_code?: string;
    region_name?: string;
    zip?: string;
  } = {};
  
  try {
    const sessionGeoData = sessionStorage.getItem('geolocation_session');
    if (sessionGeoData) {
      geolocationData = safelyExtractGeolocation(JSON.parse(sessionGeoData));
    }
  } catch (e) {
    console.warn('Failed to get geolocation from session storage:', e);
  }
  
  // Fallback to default values if no geolocation data available
  if (!geolocationData.country && !geolocationData.city) {
    geolocationData = {
      country: 'SG',
      city: 'Singapore',
      country_code: 'SG',
      country_name: 'Singapore'
    };
  }
  
  const associatedStoreId = getUserAssociatedStoreId();
  const url = payload?.url || getUrlWithParentWindowQueryParams();
  const uuid = payload?.uuid || window['uuid'];
  const organisationId =
    payload?.organisationId || window['parentId'] || window['brandId'];
    
  const additionalInfo = _.omitBy(
    {
      uuid,
      organisationId,
      storeId: _storeId
    },
    _.isUndefined
  );
  const event_timestamp = payload?.event_timestamp || getNow().getTime();

  if (payload?.event_timestamp) {
    delete payload.event_timestamp;
  }

  const event: AnalyticEvent = {
    ...additionalInfo,
    type: category,
    payload: {
      ...payload,
      env: getEnv(),
      originalUrl: window.location.href,
      url,
      userAgent: navigator.userAgent,
      storeId: _storeId,
      language: (payload || {}).language || i18n?.language || 'en',
      sessionId:
        (payload || {}).sessionId ||
        window[sessionKey] ||
        tryGetParentWindowField(sessionKey),
      associatedStoreId: associatedStoreId || undefined,
      country: geolocationData.country,
      city: geolocationData.city,
      country_code: geolocationData.country_code,
      country_name: geolocationData.country_name,
      continent_code: geolocationData.continent_code,
      continent_name: geolocationData.continent_name,
      region_code: geolocationData.region_code,
      region_name: geolocationData.region_name,
      zip: geolocationData.zip
    },
    event_timestamp,
    identityId: getIdentityId(),
    sourceSystem: isHubPage(window.location.pathname, window.location.search)
      ? 'SALES_HUB'
      : 'TOUCH_WEB'
  };
  
  // Track performance
  const processingTime = performance.now() - startTime;
  trackAnalyticsPerformance(processingTime);
  
  return event;
};

const logCustomEvent = (
  category: string,
  payload: any,
  storeId: string | undefined = undefined,
  noDelay?: boolean
) => {
  const event = getAnalyticEvent(category, payload, storeId);

  const eventType = event.type;
  const shouldDelay = !noDelay && !noDelayEvents.includes(eventType);
  if (getIdentityId()) {
    addEventToQueue(event, !shouldDelay); // flushSoon = !shouldDelay
  } else {
    logException('identityId is null in logEvent call!');
    addEventToQueue({ ...event, identityId: generateV4UUID() }, !shouldDelay);
  }
};

export type LogEvent = (
  category?: string,
  action?: string,
  payload?: any,
  noDelay?: boolean
) => void;

export const logEvent: LogEvent = (
  category = '',
  action = '',
  payload = null,
  noDelay?: boolean
) => {
  if (category) {
    logEventGA(category, action);
    const defaultPayload = {
      action: action || category
    };
    if (payload && isPlainObject(payload)) {
      logCustomEvent(
        category,
        {
          ...defaultPayload,
          ...payload
        },
        undefined, // Let getAnalyticEvent determine storeId from fallback sources
        noDelay
      );
    } else {
      logCustomEvent(category, defaultPayload, undefined, noDelay);
    }
  }
};

export const logEyeballTime = (
  durationInMs: number,
  payload: EyeballLoggerPayload<EyeballTimeView>
) => {
  logCustomEvent(EYEBALL_TIME, {
    ...payload,
    durationInMs,
    type: payload.view,
    id: payload.id
  });
};

export const logEyeballTimeOnExit = (durationInMs: number, payload) => {
  const eventPayload = {
    ...payload,
    durationInMs,
    type: payload.view,
    id: payload.id
  };
  const event = getAnalyticEvent(EYEBALL_TIME, eventPayload);
  const data = JSON.stringify([event]);
  const url = '/api/analytics';
  const signature = getRequestSignature({
    data: [event],
    method: 'POST',
    url
  });
  return fetch('/api/analytics', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-ins-date': signature.date,
      'x-ins-signature': signature.key
    },
    body: data,
    keepalive: true
  });
};

export const logKeyPress = (keyCode: string) => {
  logEvent(DID_KEY_PRESS, DID_KEY_PRESS, { keyCode });
};

export const logClickButton = (button: string) => {
  logEvent(DID_CLICK_BUTTON, DID_CLICK_BUTTON, { button });
};

export const logException = (description = '', fatal = false) => {
  if (description) {
    logExceptionGA(description, fatal);
  }
};

// Performance monitoring for analytics
const analyticsPerformanceMetrics = {
  totalEvents: 0,
  totalProcessingTime: 0,
  averageProcessingTime: 0,
  lastReportTime: 0
};

export const trackAnalyticsPerformance = (processingTime: number) => {
  analyticsPerformanceMetrics.totalEvents++;
  analyticsPerformanceMetrics.totalProcessingTime += processingTime;
  analyticsPerformanceMetrics.averageProcessingTime = 
    analyticsPerformanceMetrics.totalProcessingTime / analyticsPerformanceMetrics.totalEvents;
  
  // Log performance metrics every 100 events or every 5 minutes
  const now = Date.now();
  if (analyticsPerformanceMetrics.totalEvents % 100 === 0 || 
      (now - analyticsPerformanceMetrics.lastReportTime) > 300000) {
    analyticsPerformanceMetrics.lastReportTime = now;
  }
};

export const logJitsiEvent = (action: string, additionalInfor: object = {}) => {
  console.log(
    `%cJitsiEvent ${action} triggered`,
    'color: #FF4500',
    additionalInfor || {}
  );
  logEvent(JITSI_MEET_EVENT, action, { ...(additionalInfor || {}) });
};

// Utility function to safely extract geolocation data from various sources
const safelyExtractGeolocation = (source: any): { 
  country?: string; 
  city?: string; 
  country_code?: string; 
  country_name?: string;
  continent_code?: string;
  continent_name?: string;
  region_code?: string;
  region_name?: string;
  zip?: string;
} => {
  try {
    // If source is null/undefined, return empty object
    if (!source) return {};
    
    // If source is a string, it might be a country code
    if (typeof source === 'string') {
      return { country: source, country_code: source };
    }
    
    // If source is an object, extract the properties we need
    if (typeof source === 'object' && !Array.isArray(source)) {
      // Check for React DOM elements or corrupted objects
      if (source._wrapperState || source.nodeType || source.tagName) {
        console.warn('Detected React DOM element or corrupted object in geolocation source:', source);
        return {};
      }
      
      // Check for objects with numeric keys (corrupted)
      const keys = Object.keys(source);
      if (keys.length > 10 && keys.every(key => !isNaN(Number(key)))) {
        console.warn('Detected corrupted object with numeric keys in geolocation source:', keys.slice(0, 10));
        return {};
      }
      
      return {
        country: typeof source.country === 'string' ? source.country : (typeof source.country_code === 'string' ? source.country_code : undefined),
        city: typeof source.city === 'string' ? source.city : undefined,
        country_code: typeof source.country_code === 'string' ? source.country_code : undefined,
        country_name: typeof source.country_name === 'string' ? source.country_name : undefined,
        continent_code: typeof source.continent_code === 'string' ? source.continent_code : undefined,
        continent_name: typeof source.continent_name === 'string' ? source.continent_name : undefined,
        region_code: typeof source.region_code === 'string' ? source.region_code : undefined,
        region_name: typeof source.region_name === 'string' ? source.region_name : undefined,
        zip: typeof source.zip === 'string' ? source.zip : undefined
      };
    }
    
    return {};
  } catch (error) {
    console.warn('Error extracting geolocation data:', error);
    return {};
  }
};
